import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import { uniq } from 'lodash';

import { useButton, useI18n, useLogger } from '~Hooks';
import { getFallbackTranslation } from '~utilities';
import { TranslatedString } from '~Types';
import store from '../../store';
import { getVenues } from './__queries__';

import AutoForwarder from './AutoForwarder';
import { ALL_VENUES_QUERY } from './gql';
import {
  Wrapper,
  Header,
  VenuesContainer,
  Venue,
  VenueTitle,
  VenueLabel,
  VenueLink,
  VenueContainer,
  LogoutWrapper,
} from './styles';
import Logout from '~Scenes/Settings-v2/Components/Logout';
import { BlueButton, Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import styled from '@emotion/styled';
import Loader from '~ComponentsImproved/Loader';

const LoadingWrapper = styled(Box)`
  height: 100vh;
  background-color: var(--colorBlueGray1200);
`;

const Venues: FC = () => {
  const logger = useLogger('venues-selection');
  const locale = store.getState().locale;
  const { i18n } = useI18n();

  const venuesQuery = useQuery<getVenues>(ALL_VENUES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const retryButton = useButton({
    onClick: () => {
      logger.info('Retrying to fetch venues');
      venuesQuery.refetch();
    },
  });

  if (!venuesQuery.data || venuesQuery.loading) {
    return (
      <LoadingWrapper display="flex" justifyContent="center" alignItems="center">
        <Loader />
      </LoadingWrapper>
    );
  }

  if (venuesQuery.error) {
    logger.error('Error fetching venues', { errorMsg: venuesQuery.error });

    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        marginTop="3xl"
        padding="3xl">
        <Heading level={2} bottom={32}>
          {i18n('Venues.Error')}
        </Heading>
        <BlueButton {...retryButton} size="large">
          {i18n('General.Retry')}
        </BlueButton>
      </Box>
    );
  }

  const venues = venuesQuery.data.user.venues.filter(
    (v) => v.company.id === venuesQuery.data?.user.company.id,
  );
  const isMultiCompany =
    uniq(venues.filter((v) => v.company && v.company.id).map((v) => v.company.id)).length > 1;

  return (
    <Wrapper>
      <AutoForwarder venues={venues} user={venuesQuery.data.user} />

      <Header>{i18n('General.ChooseVenue')}</Header>

      <VenuesContainer>
        {venues
          .filter((v) => v.active && v.company)
          .map((venue) => (
            <VenueContainer key={venue.id}>
              <VenueLink to={`/venue/${venue.id}/gateway`}>
                <Venue>
                  <VenueTitle>
                    {getFallbackTranslation(venue.nameLang as TranslatedString, locale) ||
                      venue.name}
                  </VenueTitle>
                  <VenueLabel>
                    {isMultiCompany ? venue.company.name : venue.location.street || ''}
                  </VenueLabel>
                </Venue>
              </VenueLink>
            </VenueContainer>
          ))}

        {venues
          .filter((v) => !v.active)
          .map((venue) => (
            <VenueContainer key={venue.id}>
              <VenueLink to={`/venue/${venue.id}/gateway`}>
                <Venue disabled>
                  <VenueTitle>{venue.name}</VenueTitle>
                  <VenueLabel>
                    {venue.location ? venue.location.street || '' : i18n('App.NoLocation')}
                  </VenueLabel>
                </Venue>
              </VenueLink>
            </VenueContainer>
          ))}
      </VenuesContainer>

      {venues.length === 0 && (
        <h3 style={{ padding: 24, textAlign: 'center' }}>{i18n('App.NoPermission')}</h3>
      )}

      <LogoutWrapper>
        <Logout />
      </LogoutWrapper>
    </Wrapper>
  );
};

export default Venues;
